/**
 * Tanzeen+ Widget Service
 * Handles communication between the React app and native Android widget
 * 
 * Features:
 * - Updates widget prayer times data
 * - Handles deep links from widget taps
 * - Triggers widget refresh
 */

import { Capacitor, registerPlugin } from '@capacitor/core';
import { App } from '@capacitor/app';

// Register the native WidgetBridge plugin
const WidgetBridge = registerPlugin('WidgetBridge');

/**
 * Update widget data (prayer times, etc.)
 * This saves data to SharedPreferences which the Android widget reads
 */
export const updateWidgetData = async (data) => {
  if (!Capacitor.isNativePlatform()) {
    console.log('[Widget] Not on native platform, skipping widget update');
    return;
  }
  
  try {
    await WidgetBridge.updateWidgetData({
      nextPrayer: data.nextPrayer || '',
      nextPrayerTime: data.nextPrayerTime || '--:--',
      fajr: data.fajr || '',
      sunrise: data.sunrise || '',
      dhuhr: data.dhuhr || '',
      asr: data.asr || '',
      maghrib: data.maghrib || '',
      isha: data.isha || '',
    });
    
    console.log('[Widget] Data updated successfully:', data);
  } catch (error) {
    console.error('[Widget] Failed to update widget data:', error);
  }
};

/**
 * Force refresh the widget display
 */
export const refreshWidget = async () => {
  if (!Capacitor.isNativePlatform()) {
    return;
  }
  
  try {
    await WidgetBridge.refreshWidget();
    console.log('[Widget] Refresh triggered');
  } catch (error) {
    console.error('[Widget] Failed to refresh widget:', error);
  }
};

/**
 * Get current widget data
 */
export const getWidgetData = async () => {
  if (!Capacitor.isNativePlatform()) {
    return null;
  }
  
  try {
    const data = await WidgetBridge.getWidgetData();
    return data;
  } catch (error) {
    console.error('[Widget] Failed to get widget data:', error);
    return null;
  }
};

/**
 * Handle deep links from widget
 * Routes the user to the appropriate page when they tap a widget button
 */
export const setupWidgetDeepLinkHandler = (navigate) => {
  if (!Capacitor.isNativePlatform()) {
    return () => {}; // Return empty cleanup function
  }
  
  // Listen for app URL open events (deep links)
  const listener = App.addListener('appUrlOpen', ({ url }) => {
    console.log('[Widget] Deep link received:', url);
    
    try {
      // Parse the URL scheme: tanzeel://app/path or quranium://app/path
      const urlObj = new URL(url);
      let path = urlObj.pathname;
      
      // Handle case where path might be in host (tanzeel://app/prayer-times vs tanzeel://prayer-times)
      if (!path || path === '/') {
        path = urlObj.host !== 'app' ? `/${urlObj.host}` : '/';
      }
      
      if (path && path !== '/') {
        const cleanPath = path.startsWith('/') ? path : `/${path}`;
        console.log('[Widget] Navigating to:', cleanPath);
        navigate(cleanPath);
      }
    } catch (error) {
      console.error('[Widget] Failed to parse deep link:', error);
    }
  });
  
  // Check if app was opened via deep link (cold start)
  App.getLaunchUrl().then((result) => {
    if (result?.url) {
      console.log('[Widget] App launched with URL:', result.url);
      try {
        const urlObj = new URL(result.url);
        let path = urlObj.pathname;
        
        if (!path || path === '/') {
          path = urlObj.host !== 'app' ? `/${urlObj.host}` : '/';
        }
        
        if (path && path !== '/') {
          const cleanPath = path.startsWith('/') ? path : `/${path}`;
          // Small delay to ensure React Router is ready
          setTimeout(() => navigate(cleanPath), 100);
        }
      } catch (error) {
        console.error('[Widget] Failed to parse launch URL:', error);
      }
    }
  });
  
  // Return cleanup function
  return () => {
    listener.remove();
  };
};

/**
 * Calculate and format next prayer info for widget display
 */
export const calculateNextPrayer = (prayerTimes) => {
  if (!prayerTimes) return { nextPrayer: 'Next Prayer', nextPrayerTime: '--:--' };
  
  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  
  const prayers = [
    { name: 'Fajr', time: prayerTimes.Fajr || prayerTimes.fajr },
    { name: 'Sunrise', time: prayerTimes.Sunrise || prayerTimes.sunrise },
    { name: 'Dhuhr', time: prayerTimes.Dhuhr || prayerTimes.dhuhr },
    { name: 'Asr', time: prayerTimes.Asr || prayerTimes.asr },
    { name: 'Maghrib', time: prayerTimes.Maghrib || prayerTimes.maghrib },
    { name: 'Isha', time: prayerTimes.Isha || prayerTimes.isha },
  ];
  
  for (const prayer of prayers) {
    if (!prayer.time) continue;
    
    const [hours, minutes] = prayer.time.split(':').map(Number);
    const prayerMinutes = hours * 60 + minutes;
    
    if (prayerMinutes > currentMinutes) {
      return {
        nextPrayer: prayer.name,
        nextPrayerTime: prayer.time,
      };
    }
  }
  
  // If all prayers passed, next is tomorrow's Fajr
  return {
    nextPrayer: 'Fajr',
    nextPrayerTime: prayers[0]?.time || '--:--',
  };
};

export default {
  updateWidgetData,
  refreshWidget,
  getWidgetData,
  setupWidgetDeepLinkHandler,
  calculateNextPrayer,
};
